import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchNflverseStats, buildEspnNflverseLookup, getNflverseLatestSeason,
         fetchPlayerAdvancedStats, buildPlayerAdvancedLookup, fetchPlayerSeasonStats } from '../../services/nflverseService';
import { loadBase as loadEspnBase } from '../../services/espnService';
import { useAuth } from '../../context/AuthContext';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, TableSortLabel,
  Avatar, CircularProgress, Chip, TextField, InputAdornment, LinearProgress,
  Tooltip, Dialog, DialogContent, IconButton, Divider,
} from '@mui/material';

const SEASON = 2025;
const SEASON_TYPE = 2; // regular season



const POSITION_GROUPS = {
  Offense:        ['QB','RB','WR','TE','T','G','C'],
  Defense:        ['DE','DT','LB','CB','S'],
  'Special Teams':['K','P'],
};

// ── Derived stat compute functions (defined before POSITION_CONFIG) ───────
const derived = {
  intPct: (indivMap, id) => {
    const ints = parseFloat(indivMap?.[id]?.passing?.interceptions) || 0;
    const atts = parseFloat(indivMap?.[id]?.passing?.passingAttempts) || 0;
    return atts > 0 ? parseFloat((ints / atts * 100).toFixed(1)) : null;
  },
  cmpPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.passing?.completions;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  pydsPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.passing?.passingYards;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  ptdPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.passing?.passingTouchdowns;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  sckPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.passing?.sacks;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  rydsPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.rushing?.rushingYards;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  rtdPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.rushing?.rushingTouchdowns;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  carPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.rushing?.rushingAttempts;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  recPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.receiving?.receptions;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  recYdsPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.receiving?.receivingYards;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  recTdPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.receiving?.receivingTouchdowns;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  tgtPerGame: (indivMap, id) => {
    const raw = indivMap?.[id]?.receiving?.receivingTargets;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(1)) : null;
  },
  bigPlaysPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.receiving?.receivingBigPlays;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  // ── Defensive derived stats ───────────────────────────────────────────
  soloTacklesPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensive?.soloTackles;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  tflPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensive?.tacklesForLoss;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  qbHitsPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensive?.QBHits;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  hurriesPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensive?.hurries;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  stuffsPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensive?.stuffs;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
  intYdsPg: (indivMap, id) => {
    const raw = indivMap?.[id]?.defensiveInterceptions?.interceptionYards;
    if (raw == null) return null;
    const gp = parseFloat(indivMap?.[id]?.general?.gamesPlayed) || 0;
    return gp > 0 ? parseFloat((parseFloat(String(raw).replace(/,/g,'')) / gp).toFixed(2)) : null;
  },
};



// ── Column definitions per position ──────────────────────────────────────
// Two sources: 'leader' (from leaders API) or 'indiv' (from individual player stats API)
// For 'indiv', specify which category + stat name within that category

const POSITION_CONFIG = {
  QB: {
    primaryLeader: 'passingYards',
    cols: [
      { src:'indiv', cat:'general',  stat:'gamesPlayed',          label:'GP',    tip:'Games Played' },
      { src:'indiv', cat:'passing',  stat:'completions',          label:'CMP',   tip:'Completions' },
      { src:'indiv', cat:'passing',  stat:'passingAttempts',      label:'ATT',   tip:'Attempts' },
      { src:'indiv', cat:'passing',  stat:'completionPct',        label:'PCT',   tip:'Completion %' },
      { src:'leader', key:'passingYards',                         label:'PYDS',  tip:'Pass Yards' },
      { src:'indiv', cat:'passing',  stat:'passingYardsPerGame',  label:'YPG',   tip:'Yards/Game' },
      { src:'leader', key:'passingTouchdowns',                    label:'PTD',   tip:'Pass TDs' },
      { src:'indiv', cat:'passing',  stat:'interceptions',        label:'INT',   tip:'Interceptions Thrown', lowerIsBetter:true },
      { src:'derived', compute:derived.intPct, suffix:'%',        label:'INT%',  tip:'Interception Rate (INT/ATT)', lowerIsBetter:true },
      { src:'indiv', cat:'passing',  stat:'yardsPerPassAttempt',  label:'YPA',   tip:'Yards/Attempt' },
      { src:'season', key:'anyA',                                 label:'ANY/A', tip:'Adjusted Net Yards/Attempt — (Yds+20TD-45INT-SackYds)/(Att+Sacks)' },
      { src:'leader', key:'quarterbackRating',                    label:'RTG',   tip:'Passer Rating' },
      { src:'indiv', cat:'passing',  stat:'QBR',                  label:'QBR',   tip:'ESPN QBR' },
      { src:'indiv', cat:'passing',  stat:'sacks',                label:'SCK',   tip:'Sacks Taken', lowerIsBetter:true },
      { src:'indiv', cat:'rushing',  stat:'rushingYards',         label:'RYDS',  tip:'Rush Yards' },
      { src:'indiv', cat:'rushing',  stat:'rushingTouchdowns',    label:'RTD',   tip:'Rush TDs' },
      { src:'season', key:'snapPct',  fmt: v => `${v.toFixed(1)}%`, label:'SNAP%', tip:'Offensive snap share % (nflverse)' },
      { src:'season', key:'fantasyPprPg', fmt: v => v.toFixed(1),   label:'FPTS/G',tip:'Fantasy PPR points per game (nflverse)' },
      { src:'season', key:'passEpaPerAtt', fmt: v => v.toFixed(3),  label:'EPA/ATT', tip:'EPA per Pass Attempt (nflverse)', premium:true },
      { src:'pbp', key:'qb_success_rate',   label:'SR%',      tip:'QB Success Rate % (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'pbp', key:'qb_explosive_rate', label:'EXPL%',    tip:'Explosive Pass Rate ≥20 yds (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'pbp', key:'qb_pressure_rate',  label:'PRES%',    tip:'QB Pressure Rate — sacks+hits/dropbacks (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'pbp', key:'qb_shotgun_pct',    label:'SHOT%',    tip:'Shotgun % — share of dropbacks from shotgun formation (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'season', key:'dakota', fmt: v => v.toFixed(3),        label:'DAKOTA', tip:'Composite QB metric combining CPOE + EPA (nflverse)', premium:true },
      { src:'season', key:'passAirYdsPct', fmt: v => `${v.toFixed(1)}%`, label:'AIR%', tip:'% of passing yards thrown in the air (vs YAC)', premium:true },
    ],
  },
  RB: {
    primaryLeader: 'rushingYards',
    cols: [
      { src:'indiv', cat:'general',  stat:'gamesPlayed',        label:'GP',    tip:'Games Played' },
      { src:'indiv', cat:'rushing',  stat:'rushingAttempts',    label:'CAR',   tip:'Carries' },
      { src:'leader', key:'rushingYards',                       label:'RYDS',  tip:'Rush Yards' },
      { src:'season', key:'rushYdsPg', fmt: v => v.toFixed(1), label:'YPG',   tip:'Rush Yards per Game (nflverse)' },
      { src:'indiv', cat:'rushing',  stat:'yardsPerRushAttempt',label:'YPC',   tip:'Yards/Carry' },
      { src:'leader', key:'rushingTouchdowns',                  label:'RTD',   tip:'Rush TDs' },
      { src:'indiv', cat:'rushing',  stat:'rushingFumblesLost', label:'FUM',   tip:'Fumbles Lost', lowerIsBetter:true },
      { src:'indiv', cat:'receiving', stat:'receivingTargets',  label:'TGT',   tip:'Receiving Targets' },
      { src:'indiv', cat:'receiving', stat:'receptions',        label:'REC',   tip:'Receptions' },
      { src:'indiv', cat:'receiving', stat:'receivingYards',    label:'RECYDS',tip:'Rec Yards' },
      { src:'indiv', cat:'receiving', stat:'receivingTouchdowns',label:'RECTD',tip:'Rec TDs' },
      { src:'leader', key:'totalTouchdowns',                    label:'TTD',   tip:'Total TDs' },
      { src:'season', key:'snapPct',  fmt: v => `${v.toFixed(1)}%`, label:'SNAP%', tip:'Offensive snap share % (nflverse)' },
      { src:'season', key:'fantasyPprPg', fmt: v => v.toFixed(1),   label:'FPTS/G',tip:'Fantasy PPR points per game (nflverse)' },
      { src:'season', key:'rushEpaPerCar', fmt: v => v.toFixed(3),  label:'EPA/CAR', tip:'EPA per Rush Attempt (nflverse)', premium:true },
      { src:'season', key:'recEpaPerTgt',  fmt: v => v.toFixed(3),  label:'REC EPA/TGT', tip:'Receiving EPA per Target (nflverse)', premium:true },
      { src:'pbp', key:'rush_success_rate',   label:'SR%',      tip:'Rush Success Rate % (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'pbp', key:'rush_explosive_rate', label:'EXPL%',    tip:'Explosive Run Rate ≥10 yds (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
    ],
  },
  WR: {
    primaryLeader: 'receivingYards',
    cols: [
      { src:'indiv', cat:'general',  stat:'gamesPlayed',          label:'GP',    tip:'Games Played' },
      { src:'indiv', cat:'receiving',stat:'receivingTargets',     label:'TGT',   tip:'Targets' },
      { src:'leader', key:'receptions',                           label:'REC',   tip:'Receptions' },
      { src:'season', key:'catchPct', fmt: v => `${v.toFixed(1)}%`, label:'CTH%', tip:'Catch Rate — receptions/targets (nflverse)' },
      { src:'leader', key:'receivingYards',                       label:'RECYDS',tip:'Rec Yards' },
      { src:'season', key:'recYdsPg', fmt: v => v.toFixed(1),    label:'YPG',   tip:'Receiving Yards per Game (nflverse)' },
      { src:'season', key:'ypt',      fmt: v => v.toFixed(1),    label:'YPT',   tip:'Yards per Target (nflverse)' },
      { src:'indiv', cat:'receiving',stat:'yardsPerReception',    label:'YPR',   tip:'Yards/Reception' },
      { src:'leader', key:'receivingTouchdowns',                  label:'RECTD', tip:'Rec TDs' },
      { src:'indiv', cat:'receiving',stat:'receivingBigPlays',    label:'20+',   tip:'20+ Yard Plays' },
      { src:'season', key:'snapPct',  fmt: v => `${v.toFixed(1)}%`, label:'SNAP%', tip:'Offensive snap share % (nflverse)' },
      { src:'season', key:'fantasyPprPg', fmt: v => v.toFixed(1),   label:'FPTS/G',tip:'Fantasy PPR points per game (nflverse)' },
      { src:'season', key:'recEpaPerTgt', fmt: v => v.toFixed(3),   label:'EPA/TGT', tip:'EPA per Target (nflverse)', premium:true },
      { src:'season', key:'aDot',     fmt: v => v.toFixed(1),    label:'aDOT',  tip:'Average Depth of Target (nflverse)', premium:true },
      { src:'season', key:'yac',      fmt: v => v.toFixed(1),    label:'YAC',   tip:'Yards After Catch per reception (nflverse)', premium:true },
      { src:'season', key:'tgtShare', fmt: v => `${v.toFixed(1)}%`, label:'TGT%', tip:'Team target share % (nflverse)', premium:true },
      { src:'season', key:'wopr',     fmt: v => v.toFixed(3),    label:'WOPR',  tip:'Weighted Opportunity Rating — target share + air yards share (nflverse)', premium:true },
      { src:'season', key:'racr',     fmt: v => v.toFixed(3),    label:'RACR',  tip:'Receiver Air Conversion Ratio — receiving yards per air yard (nflverse)', premium:true },
      { src:'pbp', key:'rec_success_rate',   label:'SR%',   tip:'Reception Success Rate % (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
      { src:'pbp', key:'rec_explosive_rate', label:'EXPL%', tip:'Explosive Reception Rate ≥20 yds (PBP)', fmt: v => `${v.toFixed(1)}%`, premium:true },
    ],
  },
  TE: {
    primaryLeader: 'receivingYards',
    cols: [
      { src:'indiv', cat:'general',  stat:'gamesPlayed',          label:'GP',    tip:'Games Played' },
      { src:'indiv', cat:'receiving',stat:'receivingTargets',     label:'TGT',   tip:'Targets' },
      { src:'leader', key:'receptions',                           label:'REC',   tip:'Receptions' },
      { src:'season', key:'catchPct', fmt: v => `${v.toFixed(1)}%`, label:'CTH%', tip:'Catch Rate — receptions/targets (nflverse)' },
      { src:'leader', key:'receivingYards',                       label:'RECYDS',tip:'Rec Yards' },
      { src:'season', key:'recYdsPg', fmt: v => v.toFixed(1),    label:'YPG',   tip:'Receiving Yards per Game (nflverse)' },
      { src:'season', key:'ypt',      fmt: v => v.toFixed(1),    label:'YPT',   tip:'Yards per Target (nflverse)' },
      { src:'indiv', cat:'receiving',stat:'yardsPerReception',    label:'YPR',   tip:'Yards/Reception' },
      { src:'leader', key:'receivingTouchdowns',                  label:'RECTD', tip:'Rec TDs' },
      { src:'season', key:'snapPct',  fmt: v => `${v.toFixed(1)}%`, label:'SNAP%', tip:'Offensive snap share % (nflverse)' },
      { src:'season', key:'fantasyPprPg', fmt: v => v.toFixed(1),   label:'FPTS/G',tip:'Fantasy PPR points per game (nflverse)' },
      { src:'season', key:'recEpaPerTgt', fmt: v => v.toFixed(3),   label:'EPA/TGT', tip:'EPA per Target (nflverse)', premium:true },
      { src:'season', key:'aDot',     fmt: v => v.toFixed(1),    label:'aDOT',  tip:'Average Depth of Target (nflverse)', premium:true },
      { src:'season', key:'yac',      fmt: v => v.toFixed(1),    label:'YAC',   tip:'Yards After Catch per reception (nflverse)', premium:true },
      { src:'season', key:'wopr',     fmt: v => v.toFixed(3),    label:'WOPR',  tip:'Weighted Opportunity Rating (nflverse)', premium:true },
      { src:'season', key:'racr',     fmt: v => v.toFixed(3),    label:'RACR',  tip:'Receiver Air Conversion Ratio (nflverse)', premium:true },
    ],
  },
  DE: {
    primaryLeader: 'sacks',
    cols: [
      { src:'indiv', cat:'general',    stat:'gamesPlayed',    label:'GP',  tip:'Games Played' },
      { src:'leader', key:'totalTackles',                     label:'TOT', tip:'Total Tackles' },
      { src:'indiv', cat:'defensive',  stat:'soloTackles',    label:'SOLO',tip:'Solo Tackles' },
      { src:'indiv', cat:'defensive',  stat:'assistTackles',  label:'AST', tip:'Assist Tackles' },
      { src:'leader', key:'sacks',                            label:'SCK', tip:'Sacks' },
      { src:'indiv', cat:'defensive',  stat:'tacklesForLoss', label:'TFL', tip:'Tackles For Loss' },
      { src:'indiv', cat:'defensive',  stat:'QBHits',         label:'QBH', tip:'QB Hits' },
      { src:'leader', key:'passesDefended',                   label:'PD',  tip:'Passes Defended' },
      { src:'indiv', cat:'defensive',  stat:'hurries',        label:'HRY', tip:'QB Hurries' },
    ],
  },
  DT: {
    primaryLeader: 'sacks',
    cols: [
      { src:'indiv', cat:'general',    stat:'gamesPlayed',    label:'GP',  tip:'Games Played' },
      { src:'leader', key:'totalTackles',                     label:'TOT', tip:'Total Tackles' },
      { src:'indiv', cat:'defensive',  stat:'soloTackles',    label:'SOLO',tip:'Solo Tackles' },
      { src:'leader', key:'sacks',                            label:'SCK', tip:'Sacks' },
      { src:'indiv', cat:'defensive',  stat:'tacklesForLoss', label:'TFL', tip:'Tackles For Loss' },
      { src:'indiv', cat:'defensive',  stat:'QBHits',         label:'QBH', tip:'QB Hits' },
      { src:'indiv', cat:'defensive',  stat:'stuffs',         label:'STF', tip:'Run Stuffs' },
    ],
  },
  LB: {
    primaryLeader: 'totalTackles',
    cols: [
      { src:'indiv', cat:'general',    stat:'gamesPlayed',    label:'GP',  tip:'Games Played' },
      { src:'leader', key:'totalTackles',                     label:'TOT', tip:'Total Tackles' },
      { src:'indiv', cat:'defensive',  stat:'soloTackles',    label:'SOLO',tip:'Solo Tackles' },
      { src:'indiv', cat:'defensive',  stat:'assistTackles',  label:'AST', tip:'Assist Tackles' },
      { src:'leader', key:'sacks',                            label:'SCK', tip:'Sacks' },
      { src:'indiv', cat:'defensive',  stat:'tacklesForLoss', label:'TFL', tip:'Tackles For Loss' },
      { src:'leader', key:'interceptions',                    label:'INT', tip:'Interceptions' },
      { src:'leader', key:'passesDefended',                   label:'PD',  tip:'Passes Defended' },
    ],
  },
  CB: {
    primaryLeader: 'passesDefended',
    cols: [
      { src:'indiv', cat:'general',            stat:'gamesPlayed',        label:'GP',  tip:'Games Played' },
      { src:'leader', key:'passesDefended',                               label:'PD',  tip:'Passes Defended' },
      { src:'leader', key:'interceptions',                                label:'INT', tip:'Interceptions' },
      { src:'indiv', cat:'defensiveInterceptions',stat:'interceptionYards',label:'IYDS',tip:'Int Return Yards' },
      { src:'leader', key:'totalTackles',                                 label:'TOT', tip:'Total Tackles' },
      { src:'indiv', cat:'defensive',          stat:'soloTackles',        label:'SOLO',tip:'Solo Tackles' },
    ],
  },
  S: {
    primaryLeader: 'interceptions',
    cols: [
      { src:'indiv', cat:'general',            stat:'gamesPlayed',        label:'GP',  tip:'Games Played' },
      { src:'leader', key:'interceptions',                                label:'INT', tip:'Interceptions' },
      { src:'indiv', cat:'defensiveInterceptions',stat:'interceptionYards',label:'IYDS',tip:'Int Return Yards' },
      { src:'leader', key:'passesDefended',                               label:'PD',  tip:'Passes Defended' },
      { src:'leader', key:'totalTackles',                                 label:'TOT', tip:'Total Tackles' },
      { src:'indiv', cat:'defensive',          stat:'soloTackles',        label:'SOLO',tip:'Solo Tackles' },
    ],
  },
  K: {
    primaryLeader: 'totalPoints',
    cols: [
      { src:'leader', key:'totalPoints', label:'PTS', tip:'Total Points' },
    ],
  },
  P: {
    primaryLeader: 'puntYards',
    cols: [
      { src:'leader', key:'puntYards', label:'PYDS', tip:'Punt Yards' },
    ],
  },
  T: null, G: null, C: null,
};

// ── Helpers ───────────────────────────────────────────────────────────────


const INDIV_URL = id =>
  `https://sports.core.api.espn.com/v2/sports/football/leagues/nfl/seasons/${SEASON}/types/${SEASON_TYPE}/athletes/${id}/statistics/0`;

async function fetchIndivStats(id) {
  try {
    const r = await axios.get(INDIV_URL(id));
    const cats = r.data?.splits?.categories || [];
    const result = {};
    cats.forEach(cat => {
      result[cat.name] = {};
      (cat.stats||[]).forEach(s => { result[cat.name][s.name] = s.displayValue ?? String(s.value ?? '—'); });
    });
    return result;
  } catch { return {}; }
}

// ── Global data caches ────────────────────────────────────────────────────
const _posCache  = {};     // per-position individual stats

// loadBase now lives in espnService.js (shared + module-cached)
const loadBase = loadEspnBase;

async function loadPosStats(pos, leaderIds) {
  if (_posCache[pos]) return _posCache[pos];
  // Fetch individual stats for all leader IDs in parallel (batches of 20)
  const allIds = [...leaderIds];
  const indivMap = {};
  for (let i = 0; i < allIds.length; i += 20) {
    const batch = allIds.slice(i, i + 20);
    const results = await Promise.all(batch.map(id => fetchIndivStats(id).then(stats => [id, stats])));
    results.forEach(([id, stats]) => { indivMap[id] = stats; });
  }
  _posCache[pos] = indivMap;
  return indivMap;
}

// ── Nerd Rating config per position ──────────────────────────────────────
// weight: 1.0 = full, 0.5 = half; lowerIsBetter flips rank direction
const NR_CONFIG = {
  QB: {
    minQualifier: { cat:'passing', stat:'passingAttempts', pct:0.25 },
    stats: [
      { src:'derived', compute:derived.cmpPerGame,                label:'CMP/G', weight:1.0 },
      { src:'indiv',   cat:'passing', stat:'completionPct',       label:'PCT',   weight:1.0 },
      { src:'derived', compute:derived.pydsPerGame,               label:'YPG',   weight:1.0 },
      { src:'derived', compute:derived.ptdPerGame,                label:'TD/G',  weight:1.0 },
      { src:'indiv',   cat:'passing', stat:'interceptions',       label:'INT',   weight:1.5, lowerIsBetter:true },
      { src:'derived', compute:derived.intPct,                    label:'INT%',  weight:1.5, lowerIsBetter:true },
      { src:'indiv',   cat:'passing', stat:'yardsPerPassAttempt', label:'YPA',   weight:1.0 },
      { src:'derived', compute:derived.sckPerGame,                label:'SCK/G', weight:1.25, lowerIsBetter:true },
      { src:'derived', compute:derived.rydsPerGame,               label:'RYDS/G',weight:0.75 },
      { src:'derived', compute:derived.rtdPerGame,                label:'RTD/G', weight:0.5 },
      { src:'season',  key:'passEpaPerAtt',                       label:'EPA/ATT', weight:2.0 },
      { src:'pbp',     key:'qb_success_rate',                     label:'SR%',     weight:1.25 },
    ],
  },
  RB: {
    minQualifier: { cat:'rushing', stat:'rushingAttempts', pct:0.25 },
    stats: [
      { src:'derived', compute:derived.carPerGame,                              label:'CAR/G',   weight:0.75 },
      { src:'derived', compute:derived.rydsPerGame,                             label:'RYDS/G',  weight:1.0 },
      { src:'indiv',   cat:'rushing',   stat:'yardsPerRushAttempt',             label:'AVG',     weight:1.0 },
      { src:'derived', compute:derived.rtdPerGame,                              label:'RTD/G',   weight:0.75 },
      { src:'derived', compute:derived.recPerGame,                              label:'REC/G',   weight:0.75 },
      { src:'derived', compute:derived.recYdsPg,                                label:'RECYDS/G',weight:0.75 },
      { src:'indiv',   cat:'rushing',   stat:'rushingFumblesLost',              label:'FUM',     weight:1.5, lowerIsBetter:true },
      { src:'season',  key:'rushEpaPerCar',                                     label:'EPA/CAR', weight:1.5 },
      { src:'pbp',     key:'rush_success_rate',                                 label:'SR%',     weight:1.0 },
      { src:'pbp',     key:'rush_explosive_rate',                               label:'EXPL%',   weight:1.0 },
    ],
  },
  WR: {
    minQualifier: { cat:'receiving', stat:'receptions', pct:0.25 },
    stats: [
      { src:'derived', compute:derived.tgtPerGame,                              label:'TGT/G',   weight:0.75 },
      { src:'derived', compute:derived.recPerGame,                              label:'REC/G',   weight:1.0 },
      { src:'derived', compute:derived.recYdsPg,                                label:'RECYDS/G',weight:1.0 },
      { src:'indiv',   cat:'receiving', stat:'yardsPerReception',               label:'YPR',     weight:1.0 },
      { src:'derived', compute:derived.recTdPg,                                 label:'RECTD/G', weight:1.0 },
      { src:'derived', compute:derived.bigPlaysPg,                              label:'20+/G',   weight:0.5 },
      { src:'season',  key:'recEpaPerTgt',                                      label:'EPA/TGT', weight:1.5 },
      { src:'pbp',     key:'rec_success_rate',                                  label:'SR%',     weight:1.0 },
      { src:'pbp',     key:'rec_explosive_rate',                                label:'EXPL%',   weight:1.0 },
    ],
  },
  TE: {
    minQualifier: { cat:'receiving', stat:'receptions', pct:0.25 },
    stats: [
      { src:'derived', compute:derived.tgtPerGame,                              label:'TGT/G',   weight:0.75 },
      { src:'derived', compute:derived.recPerGame,                              label:'REC/G',   weight:1.0 },
      { src:'derived', compute:derived.recYdsPg,                                label:'RECYDS/G',weight:1.0 },
      { src:'indiv',   cat:'receiving', stat:'yardsPerReception',               label:'YPR',     weight:1.0 },
      { src:'derived', compute:derived.recTdPg,                                 label:'RECTD/G', weight:1.0 },
      { src:'season',  key:'recEpaPerTgt',                                      label:'EPA/TGT', weight:1.5 },
      { src:'pbp',     key:'rec_success_rate',                                  label:'SR%',     weight:1.0 },
      { src:'pbp',     key:'rec_explosive_rate',                                label:'EXPL%',   weight:1.0 },
    ],
  },
  // ── Defensive NR configs ──────────────────────────────────────────────
  // Sacks = 1.5x, Interceptions = 1.5x, all others = 1.0x
  DE: {
    minQualifier: null,
    stats: [
      { src:'derived', compute:derived.soloTacklesPg, label:'SOLO/G', weight:1.0 },
      { src:'derived', compute:derived.tflPg,         label:'TFL/G',  weight:1.25 },
      { src:'leader',  key:'sacks',                   label:'SCK',    weight:1.5 },
      { src:'derived', compute:derived.qbHitsPg,      label:'QBH/G',  weight:1.25 },
      { src:'derived', compute:derived.hurriesPg,     label:'HRY/G',  weight:1.25 },
      { src:'leader',  key:'passesDefended',          label:'PD',     weight:1.0 },
    ],
  },
  DT: {
    minQualifier: null,
    stats: [
      { src:'derived', compute:derived.soloTacklesPg, label:'SOLO/G', weight:1.0 },
      { src:'derived', compute:derived.tflPg,         label:'TFL/G',  weight:1.25 },
      { src:'leader',  key:'sacks',                   label:'SCK',    weight:1.5 },
      { src:'derived', compute:derived.qbHitsPg,      label:'QBH/G',  weight:1.25 },
    ],
  },
  LB: {
    minQualifier: null,
    stats: [
      { src:'derived', compute:derived.soloTacklesPg, label:'SOLO/G', weight:1.0 },
      { src:'derived', compute:derived.tflPg,         label:'TFL/G',  weight:1.25 },
      { src:'leader',  key:'sacks',                   label:'SCK',    weight:1.5 },
      { src:'leader',  key:'interceptions',           label:'INT',    weight:1.5 },
      { src:'leader',  key:'passesDefended',          label:'PD',     weight:1.0 },
    ],
  },
  CB: {
    minQualifier: null,
    stats: [
      { src:'leader',  key:'passesDefended',          label:'PD',     weight:1.0 },
      { src:'leader',  key:'interceptions',           label:'INT',    weight:1.5 },
      { src:'derived', compute:derived.soloTacklesPg, label:'SOLO/G', weight:1.0 },
    ],
  },
  S: {
    minQualifier: null,
    stats: [
      { src:'leader',  key:'interceptions',           label:'INT',    weight:1.5 },
      { src:'leader',  key:'passesDefended',          label:'PD',     weight:1.0 },
      { src:'derived', compute:derived.soloTacklesPg, label:'SOLO/G', weight:1.0 },
      { src:'derived', compute:derived.tflPg,         label:'TFL/G',  weight:1.25 },
    ],
  },
};

// ── Nerd Rating computation ───────────────────────────────────────────────
function computeNerdRatings(posKey, catMap, indivMap, posPlayers, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId) {
  const cfg = NR_CONFIG[posKey];
  if (!cfg || !indivMap || !catMap) return {};

  // Dynamic thresholds — scale with whatever the season leaders have done
  const withStats = posPlayers.filter(p => indivMap[p.id]);
  const maxGP = Math.max(...withStats.map(p => parseFloat(indivMap[p.id]?.general?.gamesPlayed) || 0), 1);
  const MIN_GAMES = Math.ceil(maxGP / 2);

  // Position-specific secondary qualifier (e.g. pass attempts for QB, carries for RB, catches for WR/TE)
  const mq = cfg.minQualifier;
  let minSecondary = 0;
  let getSecondary = () => 0;
  if (mq) {
    const maxSecondary = Math.max(...withStats.map(p => parseFloat(indivMap[p.id]?.[mq.cat]?.[mq.stat]) || 0), 1);
    minSecondary = Math.floor(maxSecondary * mq.pct);
    getSecondary = p => parseFloat(indivMap[p.id]?.[mq.cat]?.[mq.stat]) || 0;
  }

  const qualified = withStats.filter(p => {
    const gp = parseFloat(indivMap[p.id]?.general?.gamesPlayed) || 0;
    return gp >= MIN_GAMES && getSecondary(p) >= minSecondary;
  });
  if (qualified.length < 2) return {};

  // For each NR stat, gather all values → rank each player
  const playerStatRanks = {}; // { id: { label: rank } }

  cfg.stats.forEach(nrStat => {
    const entries = qualified.map(p => {
      let val = null;
      if (nrStat.src === 'leader') {
        val = catMap?.[nrStat.key]?.[p.id]?.num ?? null;
      } else if (nrStat.src === 'derived') {
        val = nrStat.compute ? nrStat.compute(indivMap, p.id) : null;
      } else if (nrStat.src === 'nflverse') {
        const nfl = nflverseByEspnId?.[p.id];
        val = nfl?.[nrStat.key] ?? null;
      } else if (nrStat.src === 'season') {
        const ps = playerSeasonByEspnId?.[p.id];
        val = ps?.[nrStat.key] ?? null;
      } else if (nrStat.src === 'pbp') {
        val = pbpByEspnId?.[p.id]?.[nrStat.key] ?? null;
      } else {
        const raw = indivMap?.[p.id]?.[nrStat.cat]?.[nrStat.stat];
        if (raw && raw !== '—') val = parseFloat(String(raw).replace(/,/g,'')) || 0;
      }
      return { id: p.id, val };
    }).filter(e => e.val != null);

    const sorted = [...entries].sort((a, b) =>
      nrStat.lowerIsBetter ? a.val - b.val : b.val - a.val
    );

    sorted.forEach((e, i) => {
      if (!playerStatRanks[e.id]) playerStatRanks[e.id] = {};
      playerStatRanks[e.id][nrStat.label] = i + 1;
    });
  });

  // Compute weighted average rank per player
  const scored = qualified.map(p => {
    const ranks = playerStatRanks[p.id] || {};
    let wSum = 0, wTotal = 0;
    cfg.stats.forEach(nrStat => {
      const r = ranks[nrStat.label];
      if (r != null) { wSum += r * nrStat.weight; wTotal += nrStat.weight; }
    });
    // Require at least half the stats to qualify
    const minWeight = cfg.stats.reduce((s, c) => s + c.weight, 0) / 2;
    return { id: p.id, avg: wTotal >= minWeight ? wSum / wTotal : Infinity };
  });

  // Sort by avg (lower = better), assign final NR rank + 0-100 score
  scored.sort((a, b) => a.avg - b.avg);
  const valid = scored.filter(s => s.avg !== Infinity);
  const total = valid.length;
  const nrMap = {};
  valid.forEach((s, i) => {
    // Score: rank 1 = 99.0, last = 0.0 — normalized 0-100 within position
    const score = total > 1 ? (99 * (1 - i / (total - 1))).toFixed(1) : '99.0';
    nrMap[s.id] = { rank: i + 1, of: total, avg: s.avg, score };
  });
  return nrMap;
}

// ── Rank helpers ──────────────────────────────────────────────────────────
// qualifiedOf: consistent denominator (qualified players at position) — overrides data pool size
function computeRank(col, player, catMap, indivMap, qualifiedOf) {
  let playerNum = null;
  let allNums   = [];

  if (col.src === 'leader') {
    const catData = catMap?.[col.key] || {};
    playerNum = catData[player.id]?.num ?? null;
    allNums   = Object.values(catData).map(v => v.num).filter(v => v != null && v >= 0);
  } else if (col.src === 'derived') {
    if (!col.compute) return null;
    playerNum = col.compute(indivMap, player.id);
    if (playerNum == null) return null;
    allNums = Object.keys(indivMap || {})
      .map(id => col.compute(indivMap, id))
      .filter(v => v != null && v >= 0);
  } else {
    const raw = indivMap?.[player.id]?.[col.cat]?.[col.stat];
    if (!raw || raw === '—') return null;
    playerNum = parseFloat(String(raw).replace(/,/g, '')) || 0;
    allNums = Object.values(indivMap || {})
      .map(stats => {
        const r = stats?.[col.cat]?.[col.stat];
        if (!r || r === '—') return null;
        return parseFloat(String(r).replace(/,/g, '')) || 0;
      })
      .filter(v => v != null);
  }

  if (playerNum == null || playerNum < 0) return null;

  const inv    = !!col.lowerIsBetter;
  const sorted = [...allNums].sort((a, b) => inv ? a - b : b - a);
  const rank   = (inv
    ? sorted.findIndex(v => v >= playerNum)
    : sorted.findIndex(v => v <= playerNum)) + 1 || sorted.length;

  return { rank, of: qualifiedOf || sorted.length };
}

function rankLabel(rank, of) {
  return `#${rank} of ${of}`;
}

// ── Player Card Modal ─────────────────────────────────────────────────────
function PlayerCardModal({ player, pos, cols, catMap, indivMap, posTotal, qualifiedOf, nrMap, nflverseByEspnId, nflverseSeason, pbpByEspnId, playerSeasonByEspnId, isPremium, open, onClose }) {
  if (!player) return null;

  const statCols = cols.filter(c => c.label !== 'GP');
  const nr = nrMap?.[player.id];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { backgroundColor: '#07111e', border: '1px solid #2a4560', borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ backgroundColor: '#0d1e30', borderBottom: '1px solid #2a4560', p: 2.5, pb: 1.75, position: 'relative' }}>
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: '#c0d4e8' }}>✕</IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={player.headshot} sx={{ width: 64, height: 64, border: '2px solid #4a7096' }}>
              {player.name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, color: '#ffffff' }}>{player.name}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Chip label={player.team} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#1a3040', color: '#c0d4e8', fontWeight: 700, border: '1px solid #2a4560' }} />
                <Chip label={pos} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#1a3040', color: '#c0d4e8', border: '1px solid #2a4560' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Nerd Rating */}
        <Box sx={{ mx: 2.5, mt: 2, p: 1.5, borderRadius: 2, textAlign: 'center', backgroundColor: '#0d1e30', border: '1px solid #2a4560' }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#8aaac8', fontWeight: 700, letterSpacing: '0.08em', mb: 0.5 }}>
            🧠 NERD RATING
          </Typography>
          {nr ? (
            <>
              <Typography sx={{ fontSize: '2.4rem', fontWeight: 900, lineHeight: 1, color: '#10b981', fontFamily: 'monospace' }}>
                {nr.score}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#8aaac8', mt: 0.25 }}>
                #{nr.rank} of {nr.of} qualified {pos}s
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: '#5a7a98', mt: 0.5 }}>
                0–99 scale · position-normalized · {NR_CONFIG[pos]?.stats.length}-stat formula
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#334155' }}>—</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#5a7a98' }}>
                Not enough qualifying data
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(245,166,35,0.15)', mx: 2.5, mt: 2 }} />

        {/* Stats grid */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#8aaac8', fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1.5 }}>
            {SEASON} SEASON STATS
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
            {statCols.map(col => {
              const val = player.cols?.[col.label]?.display ?? '—';
              const rk  = computeRank(col, player, catMap, indivMap, qualifiedOf);
              return (
                <Box key={col.label} sx={{
                  textAlign: 'center', p: 1, borderRadius: 1.5,
                  backgroundColor: '#0d1e30',
                  border: '1px solid #1e3448',
                }}>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: val === '—' ? '#334155' : '#ffffff', lineHeight: 1.2 }}>
                    {val}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#7a9ab8', fontWeight: 600, mt: 0.25 }}>
                    {col.tip || col.label}
                  </Typography>
                  {rk && (
                    <Typography sx={{ fontSize: '0.6rem', color: '#5a7a98', mt: 0.4, lineHeight: 1 }}>
                      {rankLabel(rk.rank, rk.of)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── EPA / Advanced Stats section ── */}
        {(() => {
          const ps  = playerSeasonByEspnId?.[player?.id];
          const pbp = pbpByEspnId?.[player?.id];
          const fmt3 = v => v != null ? (Math.abs(v) < 10 ? v.toFixed(3) : v.toFixed(1)) : null;
          const fmtPct = v => v != null ? `${v.toFixed(1)}%` : null;
          const epaStats = pos === 'QB' ? [
            { label:'EPA/Att',   val: fmt3(ps?.passEpaPerAtt),                          tip:'EPA per pass attempt (nflverse)' },
            { label:'Total EPA', val: ps?.passEpa != null ? ps.passEpa.toFixed(1) : null, tip:'Total season passing EPA (nflverse)' },
            { label:'CPOE',      val: pbp?.qb_cpoe != null ? pbp.qb_cpoe.toFixed(1) : null, tip:'Completion % over expected (PBP)' },
            { label:'SR%',       val: fmtPct(pbp?.qb_success_rate),   tip:'QB success rate on dropbacks (PBP)' },
            { label:'EXPL%',     val: fmtPct(pbp?.qb_explosive_rate), tip:'Explosive pass rate ≥20 yds (PBP)' },
            { label:'PRES%',     val: fmtPct(pbp?.qb_pressure_rate),  tip:'QB pressure rate — sacks+hits/dropbacks (PBP)' },
          ] : pos === 'RB' ? [
            { label:'EPA/Car',  val: fmt3(ps?.rushEpaPerCar),              tip:'EPA per rush attempt (nflverse)' },
            { label:'SR%',      val: fmtPct(pbp?.rush_success_rate),       tip:'Rush success rate (PBP)' },
            { label:'EXPL%',    val: fmtPct(pbp?.rush_explosive_rate),     tip:'Explosive run rate ≥10 yds (PBP)' },
            { label:'YPC',      val: pbp?.rush_ypc != null ? pbp.rush_ypc.toFixed(1) : null, tip:'Yards per carry (PBP)' },
          ] : ['WR','TE'].includes(pos) ? [
            { label:'EPA/Tgt',  val: fmt3(ps?.recEpaPerTgt),           tip:'EPA per target (nflverse)' },
            { label:'aDOT',     val: ps?.aDot != null ? ps.aDot.toFixed(1) : null, tip:'Avg depth of target (nflverse)' },
            { label:'YAC/Rec',  val: ps?.yac  != null ? ps.yac.toFixed(1)  : null, tip:'Yards after catch per reception (nflverse)' },
            { label:'SR%',      val: fmtPct(pbp?.rec_success_rate),    tip:'Reception success rate (PBP)' },
            { label:'EXPL%',    val: fmtPct(pbp?.rec_explosive_rate),  tip:'Explosive reception rate ≥20 yds (PBP)' },
          ] : [];

          if (!epaStats.length) return null;
          const visibleStats = epaStats.filter(s => s.val != null);

          return (
            <>
              <Divider sx={{ borderColor: 'rgba(245,166,35,0.15)', mx: 2.5 }} />
              <Box sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5 }}>
                  <Typography sx={{ fontSize:'0.68rem', color:'#8aaac8', fontWeight:700, letterSpacing:'0.08em' }}>
                    ⚡ ADVANCED STATS {nflverseSeason ? `(${nflverseSeason})` : ''}
                  </Typography>
                  {!isPremium && (
                    <Chip label="Nerds+" color="secondary" size="small" sx={{ fontSize:'0.55rem', height:16, fontWeight:800 }} />
                  )}
                </Box>
                {isPremium && ps ? (
                  <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${Math.max(visibleStats.length, 1)}, 1fr)`, gap:0.75 }}>
                    {visibleStats.map(s => (
                      <Tooltip key={s.label} title={s.tip} placement="top" arrow>
                        <Box sx={{ textAlign:'center', p:1, borderRadius:1.5, backgroundColor:'#0d1e30', border:'1px solid #1e3448' }}>
                          <Typography sx={{ fontSize:'1.05rem', fontWeight:800, color:'#ffffff', lineHeight:1.2 }}>
                            {s.val}
                          </Typography>
                          <Typography sx={{ fontSize:'0.6rem', color:'#7a9ab8', fontWeight:600, mt:0.25 }}>{s.label}</Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign:'center', py:1.5, px:1, borderRadius:2, backgroundColor:'#0d1e30', border:'1px solid #1e3448' }}>
                    <Typography sx={{ fontSize:'0.75rem', color:'#5a7a98' }}>
                      🔒 EPA & advanced stats unlock with Nerds+
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}

        <Box sx={{ px: 2.5, pb: 2, textAlign: 'center', borderTop: '1px solid #1e3448', pt: 1.5 }}>
          <Typography sx={{ fontSize: '0.62rem', color: '#334155' }}>
            Full player card with snap trends & advanced grades — coming soon
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── OL Placeholder ────────────────────────────────────────────────────────
function OLPlaceholder({ pos }) {
  const names = { T:'Offensive Tackles', G:'Guards', C:'Centers' };
  return (
    <Box sx={{ py:6, textAlign:'center' }}>
      <Typography sx={{ fontSize:'2rem', mb:1 }}>🛡️</Typography>
      <Typography variant="h6" sx={{ fontWeight:700, mb:0.5 }}>{pos} — {names[pos]}</Typography>
      <Typography variant="body2" sx={{ color:'#94a3b8', mb:2, maxWidth:400, mx:'auto' }}>
        Blocking metrics aren't in public data. Pass-block win rate, run-block grade, and pressure allowed are on the Nerds+ roadmap.
      </Typography>
      <Chip label="Coming Soon · Nerds+" color="secondary" size="small" sx={{ fontWeight:700 }} />
    </Box>
  );
}

const TH = {
  backgroundColor:'#131318', color:'#94a3b8',
  fontWeight:700, fontSize:'0.7rem', py:0.75,
  borderBottom:'2px solid #4a7096', whiteSpace:'nowrap',
  position:'sticky', top:0, zIndex:2,
};

// Frozen column widths
const COL_W = { rank: 36, nr: 68, player: 178 };




function getColValue(col, player, catMap, indivMap, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId) {
  if (col.src === 'leader') {
    return { display: catMap[col.key]?.[player.id]?.display ?? '—', num: catMap[col.key]?.[player.id]?.num ?? -1 };
  }
  if (col.src === 'derived') {
    const num = col.compute ? col.compute(indivMap, player.id) : null;
    if (num == null) return { display:'—', num:-1 };
    return { display: col.suffix ? `${num}${col.suffix}` : String(num), num };
  }
  if (col.src === 'nflverse') {
    const nfl = nflverseByEspnId?.[player.id];
    const val = nfl?.[col.key] ?? null;
    if (val == null) return { display:'—', num:-1 };
    const display = Number.isInteger(val) ? String(val)
      : Math.abs(val) < 10 ? val.toFixed(3)
      : val.toFixed(1);
    return { display, num: val };
  }
  if (col.src === 'season') {
    const ps = playerSeasonByEspnId?.[player.id];
    const val = ps?.[col.key] ?? null;
    if (val == null) return { display:'—', num:-1 };
    const display = col.fmt ? col.fmt(val)
      : Number.isInteger(val) ? String(val)
      : Math.abs(val) < 10 ? val.toFixed(3)
      : val.toFixed(1);
    return { display, num: val };
  }
  if (col.src === 'pbp') {
    const val = pbpByEspnId?.[player.id]?.[col.key] ?? null;
    if (val == null) return { display:'—', num:-1 };
    const display = col.fmt ? col.fmt(val) : val.toFixed(1);
    return { display, num: val };
  }
  // individual
  const pStats = indivMap?.[player.id];
  const raw = pStats?.[col.cat]?.[col.stat];
  if (raw == null || raw === '') return { display:'—', num:-1 };
  const num = parseFloat(raw.replace(/,/g,'')) || 0;
  return { display: raw, num };
}

// ── Scrollbar shared styles ───────────────────────────────────────────────
const SCROLLBAR_SX = {
  scrollbarWidth: 'thick',
  scrollbarColor: '#4a7096 rgba(8,18,32,0.7)',
  '&::-webkit-scrollbar':              { height: 22 },
  '&::-webkit-scrollbar-track':        { background: 'rgba(8,18,32,0.7)', borderRadius: 11 },
  '&::-webkit-scrollbar-thumb':        { background: 'rgba(245,166,35,0.4)', borderRadius: 11, border: '3px solid rgba(8,18,32,0.7)' },
  '&::-webkit-scrollbar-thumb:hover':  { background: '#2ea84b' },
  '&::-webkit-scrollbar-thumb:active': { background: '#279242' },
};

// Frozen panel total width — used to offset the top scrollbar
const FROZEN_W = COL_W.rank + COL_W.nr + COL_W.player;

const HDR_H = 38;
const ROW_H = 41;

// ── NR Column header tooltip ──────────────────────────────────────────────
const NR_TOOLTIP = "Nerd Rating — FFN's proprietary positional rank. Combines efficiency, opportunity, and impact into a single score. Only players with 40%+ of team snaps qualify. Formula in development.";

// ── Position Table ────────────────────────────────────────────────────────
function PositionTable({ pos, catMap, players, indivMap, loadingIndiv, nflverseByEspnId, nflverseSeason, pbpByEspnId, playerSeasonByEspnId, isPremium }) {
  const config = POSITION_CONFIG[pos];
  const [rows, setRows]               = useState([]);
  const [orderBy, setOrderBy]         = useState(null);
  const [order, setOrder]             = useState('desc');
  const [search, setSearch]           = useState('');
  const [modalPlayer, setModalPlayer] = useState(null);
  const [hoveredIdx, setHoveredIdx]   = useState(null);
  const [nrMap, setNrMap]             = useState({});
  const [ghostW, setGhostW]           = useState(0);
  const topScrollRef  = useRef(null);
  const botScrollRef  = useRef(null);
  const leftTableRef  = useRef(null);
  const rightTableRef = useRef(null);

  // Callback ref: fires when the stats table div mounts/changes — measures width for top ghost scrollbar
  const innerTableRef = React.useCallback(node => {
    if (!node) return;
    const update = () => setGhostW(node.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync row heights between frozen and stats panels after every render
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const leftRows  = leftTableRef.current?.querySelectorAll('tr');
      const rightRows = rightTableRef.current?.querySelectorAll('tr');
      if (!leftRows || !rightRows) return;
      // Reset first so measurements reflect natural height
      leftRows.forEach(r  => (r.style.height = ''));
      rightRows.forEach(r => (r.style.height = ''));
      const count = Math.min(leftRows.length, rightRows.length);
      for (let i = 0; i < count; i++) {
        const h = Math.max(leftRows[i].offsetHeight, rightRows[i].offsetHeight);
        leftRows[i].style.height  = h + 'px';
        rightRows[i].style.height = h + 'px';
      }
    });
    return () => cancelAnimationFrame(raf);
  });  // runs after every render — intentional

  const onTopScroll = (e) => { if (botScrollRef.current) botScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; };
  const onBotScroll = (e) => { if (topScrollRef.current) topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; };

  // Compute Nerd Ratings when individual stats or nflverse data are available
  useEffect(() => {
    if (!catMap || !indivMap || !players) return;
    const posPlayers = Object.values(players).filter(p => p.posKey === pos);
    const map = computeNerdRatings(pos, catMap, indivMap, posPlayers, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId);
    setNrMap(map);
  }, [pos, catMap, indivMap, players, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!config || !catMap || !players) return;
    const posPlayers = Object.values(players).filter(p => p.posKey === pos);
    const built = posPlayers.map(p => {
      const cols = {};
      config.cols.forEach(col => { cols[col.label] = getColValue(col, p, catMap, indivMap, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId); });
      return { ...p, cols };
    });
    const primaryCol = config.cols[0];
    built.sort((a,b) => (b.cols[config.cols[1]?.label]?.num ?? b.cols[primaryCol.label]?.num ?? -1)
                      - (a.cols[config.cols[1]?.label]?.num ?? a.cols[primaryCol.label]?.num ?? -1));
    setRows(built);
    setOrderBy(null);
  }, [pos, catMap, players, indivMap, config, nflverseByEspnId, pbpByEspnId, playerSeasonByEspnId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sort by NR when first computed (if user hasn't manually sorted)
  // Must be before early returns to satisfy Rules of Hooks
  useEffect(() => {
    if (Object.keys(nrMap).length === 0) return;
    if (orderBy !== null) return;
    setOrderBy('NR');
    setOrder('asc');
    setRows(prev => [...prev].sort((a, b) =>
      (nrMap[a.id]?.rank ?? 9999) - (nrMap[b.id]?.rank ?? 9999)
    ));
  }, [nrMap]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!config) return <OLPlaceholder pos={pos} />;
  if (!rows.length) return <Box sx={{ py:4, display:'flex', justifyContent:'center' }}><CircularProgress size={24} color="primary"/></Box>;

  const handleSort = label => {
    const isDesc = orderBy === label && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(label);
    setRows(prev => [...prev].sort((a,b) => {
      const av = a.cols[label]?.num ?? -1;
      const bv = b.cols[label]?.num ?? -1;
      return isDesc ? av - bv : bv - av;
    }));
  };

  const handleNrSort = () => {
    const isAsc = orderBy === 'NR' && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy('NR');
    setRows(prev => [...prev].sort((a, b) => {
      const ar = nrMap[a.id]?.rank ?? 9999;
      const br = nrMap[b.id]?.rank ?? 9999;
      return isAsc ? br - ar : ar - br;
    }));
  };

  const filtered = search
    ? rows.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.team.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const rowBg  = (i) => hoveredIdx === i ? '#1c1c24' : '#0e0e12';
  const frozenBg = (i) => hoveredIdx === i ? '#1e3a5a' : '#131318';

  return (
    <Box>
      {loadingIndiv && <LinearProgress color="primary" sx={{ height:2 }} />}

      {/* ── Search / status bar ── */}
      <Box sx={{ px:2, py:1, borderBottom:'1px solid rgba(245,166,35,0.35)', display:'flex', alignItems:'center', gap:2, backgroundColor:'#131318' }}>
        <TextField size="small" placeholder="Search player or team…" value={search} onChange={e=>setSearch(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start">🔍</InputAdornment> }}
          sx={{ width:240, '& .MuiOutlinedInput-root':{ backgroundColor:'#0e0e12' }, '& .MuiOutlinedInput-root fieldset':{ borderColor:'rgba(245,166,35,0.4)' } }}
        />
        <Typography variant="caption" sx={{ color:'#94a3b8' }}>
          {filtered.length} players
          {loadingIndiv && <span style={{color:'#f5a623'}}> · loading detailed stats…</span>}
        </Typography>
        <Chip label={`${SEASON} Season`} size="small" color="success" sx={{ fontSize:'0.6rem', height:18, ml:'auto' }} />
        {playerSeasonByEspnId && nflverseSeason && (
          <Tooltip title={`EPA & advanced stats from nflverse pre-processed data (${nflverseSeason} season)`} placement="top" arrow>
            <Chip label={`⚡ nflverse ${nflverseSeason}`} size="small"
              sx={{ fontSize:'0.6rem', height:18, bgcolor:'#1c1c24', color: isPremium ? '#2ea84b' : '#4a6080', cursor:'default' }} />
          </Tooltip>
        )}
      </Box>

      {/* ── Top scrollbar — offset to start at stats columns ── */}
      <Box sx={{ display:'flex' }}>
        <Box sx={{ flexShrink: 0, width: FROZEN_W }} /> {/* spacer matching frozen panel */}
        <Box
          ref={topScrollRef}
          onScroll={onTopScroll}
          sx={{ flex: 1, overflowX: 'scroll', overflowY: 'hidden', height: 30, ...SCROLLBAR_SX }}
        >
          <div style={{ width: ghostW, height: 1, minWidth: '100%' }} />
        </Box>
      </Box>

      {/* ── Two-panel layout: frozen left | scrollable right ── */}
      <Box sx={{ display:'flex', alignItems:'flex-start', maxHeight: 580, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.25)', borderRadius: 3 },
      }}>

        {/* ── LEFT: Frozen panel (#, NR, Player) ── */}
        <Box sx={{
          flexShrink: 0,
          width: COL_W.rank + COL_W.nr + COL_W.player,
          boxShadow: '4px 0 12px rgba(0,0,0,0.5)',
          zIndex: 3,
          position: 'relative',
        }}>
          <Table ref={leftTableRef} size="small" stickyHeader sx={{ tableLayout:'fixed', width: COL_W.rank + COL_W.nr + COL_W.player }}>
            <TableHead>
              <TableRow sx={{ height: HDR_H }}>
                <TableCell sx={{ ...TH, width: COL_W.rank }}>#</TableCell>
                <TableCell align="center" sx={{ ...TH, width: COL_W.nr, cursor:'pointer', userSelect:'none' }}
                  onClick={handleNrSort}>
                  <Tooltip title={NR_TOOLTIP} placement="top" arrow
                    componentsProps={{ tooltip:{ sx:{ maxWidth:260, fontSize:'0.72rem', lineHeight:1.5 } } }}>
                    <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5 }}>
                      <span>🧠</span><span>NR</span>
                      <span style={{ fontSize:'0.6rem', color: orderBy==='NR' ? '#2ea84b' : '#f5a623', marginLeft:1 }}>
                        {orderBy==='NR' ? (order==='asc' ? '↑' : '↓') : 'ⓘ'}
                      </span>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...TH, width: COL_W.player }}>Player</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p, i) => (
                <TableRow key={p.id}
                  onClick={() => setModalPlayer(p)}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  sx={{ height: ROW_H, cursor:'pointer', backgroundColor: frozenBg(i), transition:'background-color 0.1s' }}
                >
                  <TableCell sx={{ color:'#f5a623', fontWeight:700, py:0, fontSize:'0.78rem', width: COL_W.rank, backgroundColor:'inherit' }}>{i+1}</TableCell>
                  <TableCell align="center" sx={{ py:0, width: COL_W.nr, backgroundColor:'inherit' }}>
                    {nrMap[p.id] ? (
                      <Box>
                        <Typography sx={{ fontSize:'0.78rem', fontWeight:800, lineHeight:1.1, color:'#10b981', fontFamily:'monospace' }}>
                          {nrMap[p.id].score}
                        </Typography>
                        <Typography sx={{ fontSize:'0.55rem', color:'#64748b', lineHeight:1 }}>
                          #{nrMap[p.id].rank}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize:'0.72rem', color:'#334155', fontWeight:600 }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py:0, width: COL_W.player, backgroundColor:'inherit' }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      {p.headshot
                        ? <Avatar src={p.headshot} sx={{ width:28, height:28 }}/>
                        : <Avatar sx={{ width:28, height:28, fontSize:'0.65rem', bgcolor:'#1c1c24' }}>{p.name[0]}</Avatar>}
                      <Box sx={{ overflow:'hidden' }}>
                        <Typography variant="caption" sx={{ fontWeight:700, display:'block', fontSize:'0.82rem', color:'#e8f0f8', lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.shortName}</Typography>
                        <Typography variant="caption" sx={{ color:'#94a3b8', fontSize:'0.65rem' }}>{p.team}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* ── RIGHT: Scrollable stats panel (bottom scrollbar only) ── */}
        <Box
          ref={botScrollRef}
          onScroll={onBotScroll}
          sx={{ flex: 1, minWidth: 0, overflowX: 'scroll', ...SCROLLBAR_SX }}
        >
          <div ref={innerTableRef}>
            <Table ref={rightTableRef} size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ height: HDR_H }}>
                  {config.cols.map(col => (
                    <TableCell key={col.label} align="right"
                      sx={{ ...TH, color: col.premium && !isPremium ? '#2ea84b88' : TH.color }}
                      title={col.tip}>
                      <TableSortLabel
                        active={orderBy === col.label}
                        direction={orderBy === col.label ? order : 'desc'}
                        onClick={() => !col.premium || isPremium ? handleSort(col.label) : null}
                        sx={{ '& .MuiTableSortLabel-icon':{ color:'#2ea84b !important' }, color:'inherit', cursor: col.premium && !isPremium ? 'default' : 'pointer' }}
                      >
                        {col.premium && !isPremium ? `🔒 ${col.label}` : col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((p, i) => (
                  <TableRow key={p.id}
                    onClick={() => setModalPlayer(p)}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    sx={{ height: ROW_H, cursor:'pointer', backgroundColor: rowBg(i), transition:'background-color 0.1s' }}
                  >
                    {config.cols.map(col => {
                      const locked = col.premium && !isPremium;
                      const val    = p.cols[col.label]?.display;
                      return (
                        <TableCell key={col.label} align="right" sx={{ py:0, fontSize:'0.82rem', fontVariantNumeric:'tabular-nums',
                          color: locked ? '#2ea84b55' : val === '—' ? '#334155' : '#e8f0f8' }}>
                          {locked ? (
                            <Tooltip title="Nerds+ — upgrade to unlock EPA & advanced stats" placement="top" arrow>
                              <span style={{ cursor:'pointer', fontSize:'0.75rem' }}>🔒</span>
                            </Tooltip>
                          ) : val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Box>

      </Box>

      {/* Player card modal */}
      <PlayerCardModal
        player={modalPlayer}
        pos={pos}
        cols={config?.cols || []}
        catMap={catMap}
        indivMap={indivMap}
        nrMap={nrMap}
        nflverseByEspnId={nflverseByEspnId}
        nflverseSeason={nflverseSeason}
        pbpByEspnId={pbpByEspnId}
        playerSeasonByEspnId={playerSeasonByEspnId}
        isPremium={isPremium}
        posTotal={Object.values(players || {}).filter(p => p.posKey === pos).length}
        qualifiedOf={
          Object.keys(indivMap || {}).length ||
          Object.keys(catMap?.[config?.primaryLeader] || {}).length
        }
        open={!!modalPlayer}
        onClose={() => setModalPlayer(null)}
      />
    </Box>
  );
}

// ── Root component ────────────────────────────────────────────────────────
const GROUP_LABELS = Object.keys(POSITION_GROUPS);

export default function PlayerStatsTable() {
  const [group, setGroup]       = useState(0);
  const [pos, setPos]           = useState('QB');
  const [catMap, setCatMap]     = useState(null);
  const [players, setPlayers]   = useState(null);
  const [indivMap, setIndivMap] = useState(null);
  const [baseLoading, setBaseLoading] = useState(true);
  const [indivLoading, setIndivLoading] = useState(false);
  const [error, setError]       = useState(null);
  const [nflverseByEspnId, setNflverseByEspnId] = useState(null);
  const [nflverseSeason, setNflverseSeason]     = useState(null);
  const [pbpByEspnId, setPbpByEspnId]           = useState(null);
  const [playerSeasonByEspnId, setPlayerSeasonByEspnId] = useState(null);
  const lastPos = useRef(null);
  const { isPremium } = useAuth();

  // Load base data once
  useEffect(() => {
    loadBase()
      .then(({ catMap, players }) => {
        setCatMap(catMap);
        setPlayers(players);
        setBaseLoading(false);
        // Kick off nflverse + PBP advanced stats in parallel — non-blocking
        fetchNflverseStats()
          .then(nflMap => {
            const lookup = buildEspnNflverseLookup(nflMap, players);
            setNflverseByEspnId(lookup);
            setNflverseSeason(getNflverseLatestSeason());
          })
          .catch(e => console.warn('[nflverse] Failed to load advanced stats:', e));

        fetchPlayerAdvancedStats()
          .then(advData => {
            const lookup = buildPlayerAdvancedLookup(advData, players);
            setPbpByEspnId(lookup);
          })
          .catch(e => console.warn('[nflverse] Failed to load PBP player stats:', e));

        fetchPlayerSeasonStats()
          .then(({ lookup, season }) => {
            setPlayerSeasonByEspnId(lookup);
            // Use season from pre-processed JSON as primary source (nflverse CSV is CORS-blocked in prod)
            if (season) setNflverseSeason(season);
          })
          .catch(e => console.warn('[player_season] Failed to load season stats:', e));
      })
      .catch(() => { setError('Could not load player data.'); setBaseLoading(false); });
  }, []);

  // Load individual stats when position changes
  useEffect(() => {
    if (!catMap || !pos || lastPos.current === pos) return;
    const config = POSITION_CONFIG[pos];
    if (!config) return;

    lastPos.current = pos;
    const leaderIds = Object.keys(catMap[config.primaryLeader] || {});
    if (!leaderIds.length) return;

    if (_posCache[pos]) { setIndivMap(_posCache[pos]); return; }

    setIndivLoading(true);
    setIndivMap(null);
    loadPosStats(pos, leaderIds)
      .then(m => { setIndivMap(m); setIndivLoading(false); })
      .catch(() => setIndivLoading(false));
  }, [pos, catMap]);

  if (baseLoading) return (
    <Box sx={{ py:8, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <CircularProgress color="primary"/>
      <Typography variant="caption" sx={{ color:'#94a3b8' }}>Loading 2025 season rosters…</Typography>
    </Box>
  );
  if (error) return <Typography sx={{ p:3, color:'#f55' }}>{error}</Typography>;

  const positions = POSITION_GROUPS[GROUP_LABELS[group]];

  const handleGroupChange = (_, v) => {
    setGroup(v);
    const newPos = POSITION_GROUPS[GROUP_LABELS[v]][0];
    setPos(newPos);
    lastPos.current = null;
  };
  const handlePosChange = p => { setPos(p); lastPos.current = null; };

  return (
    <Box>
      <Tabs value={group} onChange={handleGroupChange} textColor="inherit" indicatorColor="primary"
        sx={{ borderBottom:'1px solid rgba(245,166,35,0.35)', '& .MuiTab-root':{ textTransform:'none', fontWeight:700, fontSize:'0.88rem', minHeight:40, py:0 } }}
      >
        {GROUP_LABELS.map(g => <Tab key={g} label={g}/>)}
      </Tabs>

      <Box sx={{ display:'flex', gap:0.75, flexWrap:'wrap', px:1.5, py:1.25, borderBottom:'1px solid rgba(245,166,35,0.35)', backgroundColor:'#131318' }}>
        {positions.map(p => (
          <Box key={p} onClick={() => handlePosChange(p)} sx={{
            px:1.5, py:0.35, borderRadius:1.5, cursor:'pointer', fontSize:'0.8rem', fontWeight:700,
            backgroundColor: pos===p ? '#2ea84b' : 'rgba(74,112,150,0.18)',
            color: pos===p ? '#fff' : '#94a3b8',
            border:`1px solid ${pos===p ? '#2ea84b' : 'rgba(245,166,35,0.4)'}`,
            transition:'all 0.12s', userSelect:'none',
            '&:hover':{ backgroundColor: pos===p ? '#279242' : 'rgba(74,112,150,0.35)' },
          }}>{p}</Box>
        ))}
      </Box>

      <PositionTable key={pos} pos={pos} catMap={catMap} players={players} indivMap={indivMap} loadingIndiv={indivLoading} nflverseByEspnId={nflverseByEspnId} nflverseSeason={nflverseSeason} pbpByEspnId={pbpByEspnId} playerSeasonByEspnId={playerSeasonByEspnId} isPremium={isPremium} />
    </Box>
  );
}
